import { useCallback, useEffect, useRef, useState } from "react";
import { FiChevronLeft, FiChevronRight, FiMaximize2, FiX } from "react-icons/fi";

import FallbackImage from "newui/components/FallbackImage/FallbackImage";

interface ImageCarouselProps {
	alt: string;
	/** Project name overlaid on the image (bottom-left, white text over gradient) */
	heading?: string;
	height?: string;
	images: string[];
	showLargeImagePreview?: boolean;
	/** Location string overlaid below the heading */
	subheading?: string;
}

/** Bottom text overlay with semi-transparent background. */
const TextOverlay: React.FC<{ heading?: string; subheading?: string }> = ({
	heading,
	subheading,
}) => {
	if (!heading && !subheading) return null;
	return (
		<div className="absolute bottom-3 left-3 z-[6] rounded-lg bg-black/50 px-3 py-2 text-white backdrop-blur-sm">
			{heading && (
				<p className="text-base font-semibold leading-tight">{heading}</p>
			)}
			{subheading && (
				<p className="mt-0.5 text-sm opacity-90">{subheading}</p>
			)}
		</div>
	);
};

/** Full-screen lightbox for viewing images at full resolution. */
const Lightbox: React.FC<{
	activeIndex: number;
	alt: string;
	images: string[];
	onClose: () => void;
	onNext: () => void;
	onPrev: () => void;
}> = ({ images, alt, activeIndex, onClose, onPrev, onNext }) => {
	const total = images.length;
	const touchStartRef = useRef<{ x: number; y: number } | null>(null);

	// Keyboard navigation
	useEffect(() => {
		const handleKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") onClose();
			if (e.key === "ArrowLeft") onPrev();
			if (e.key === "ArrowRight") onNext();
		};
		document.addEventListener("keydown", handleKey);
		return () => document.removeEventListener("keydown", handleKey);
	}, [onClose, onPrev, onNext]);

	// Prevent body scroll while open
	useEffect(() => {
		document.body.style.overflow = "hidden";
		return () => {
			document.body.style.overflow = "";
		};
	}, []);

	const handleTouchStart = useCallback((e: React.TouchEvent) => {
		touchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
	}, []);

	const handleTouchEnd = useCallback((e: React.TouchEvent) => {
		if (!touchStartRef.current) return;
		const dx = e.changedTouches[0].clientX - touchStartRef.current.x;
		const dy = e.changedTouches[0].clientY - touchStartRef.current.y;
		touchStartRef.current = null;

		// Only register horizontal swipes (ignore vertical scrolls)
		if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy)) return;

		if (dx < 0) onNext();
		else onPrev();
	}, [onNext, onPrev]);

	return (
		<div
			className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/90"
			onClick={(e) => {
				e.stopPropagation();
				onClose();
			}}
			onTouchStart={handleTouchStart}
			onTouchEnd={handleTouchEnd}
		>
			{/* Close button */}
			<button
				type="button"
				onClick={onClose}
				className="absolute right-4 top-4 z-[1101] rounded-full bg-white/20 p-2 text-white backdrop-blur-sm transition-colors hover:bg-white/40"
				aria-label="Close preview"
			>
				<FiX size={24} />
			</button>

			{/* Counter */}
			{total > 1 && (
				<div className="absolute top-4 left-1/2 -translate-x-1/2 z-[1101] rounded-full bg-white/20 px-3 py-1 text-sm text-white backdrop-blur-sm">
					{activeIndex + 1} / {total}
				</div>
			)}

			{/* Image */}
			<div
				className="flex max-h-[85vh] max-w-[90vw] items-center justify-center"
				onClick={(e) => e.stopPropagation()}
			>
				<FallbackImage
					src={images[activeIndex]}
					alt={`${alt} ${activeIndex + 1}`}
					className="max-h-[85vh] max-w-[90vw] rounded-lg object-contain select-none"
					draggable={false}
				/>
			</div>

			{/* Left arrow */}
			{activeIndex > 0 && (
				<button
					type="button"
					onClick={(e) => {
						e.stopPropagation();
						onPrev();
					}}
					className="absolute left-3 top-1/2 z-[1101] -translate-y-1/2 rounded-full bg-white/20 p-2.5 text-white backdrop-blur-sm transition-colors hover:bg-white/40"
					aria-label="Previous image"
				>
					<FiChevronLeft size={24} />
				</button>
			)}

			{/* Right arrow */}
			{activeIndex < total - 1 && (
				<button
					type="button"
					onClick={(e) => {
						e.stopPropagation();
						onNext();
					}}
					className="absolute right-3 top-1/2 z-[1101] -translate-y-1/2 rounded-full bg-white/20 p-2.5 text-white backdrop-blur-sm transition-colors hover:bg-white/40"
					aria-label="Next image"
				>
					<FiChevronRight size={24} />
				</button>
			)}
		</div>
	);
};

/**
 * Image carousel with left/right arrow buttons, swipe-scroll support,
 * dot indicators, optional heading/subheading overlay, and full-screen lightbox on click.
 */
export const ImageCarousel: React.FC<ImageCarouselProps> = ({
	images,
	alt,
	height = "h-48",
	heading,
	subheading,
	showLargeImagePreview = true
}) => {
	const scrollRef = useRef<HTMLDivElement>(null);
	const [activeIndex, setActiveIndex] = useState(0);
	const [lightboxOpen, setLightboxOpen] = useState(false);
	const [lightboxIndex, setLightboxIndex] = useState(0);
	const total = images.length;

	const scrollTo = useCallback(
		(index: number) => {
			const container = scrollRef.current;
			if (!container) return;
			const clamped = Math.max(0, Math.min(index, total - 1));
			const child = container.children[clamped] as HTMLElement | undefined;
			if (child) {
				child.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "start" });
			}
			setActiveIndex(clamped);
		},
		[total],
	);

	const handleScroll = useCallback(() => {
		const container = scrollRef.current;
		if (!container) return;
		const scrollLeft = container.scrollLeft;
		const width = container.offsetWidth;
		const newIndex = Math.round(scrollLeft / width);
		setActiveIndex(Math.max(0, Math.min(newIndex, total - 1)));
	}, [total]);

	const openLightbox = useCallback(() => {
		setLightboxIndex(activeIndex);
		setLightboxOpen(true);
	}, [activeIndex]);

	const closeLightbox = useCallback(() => {
		setLightboxOpen(false);
		scrollTo(lightboxIndex);
	}, [lightboxIndex, scrollTo]);

	const lightboxPrev = useCallback(() => {
		setLightboxIndex((prev) => Math.max(0, prev - 1));
	}, []);

	const lightboxNext = useCallback(() => {
		setLightboxIndex((prev) => Math.min(total - 1, prev + 1));
	}, [total]);

	if (total === 0) return null;

	const hasOverlay = !!(heading || subheading);

	// Single image — no carousel controls needed
	if (total === 1) {
		return (
			<>
				<div
					className={`${height} group/img relative w-full`}
				>
					<FallbackImage
						src={images[0]}
						alt={`${alt} 1`}
						className="h-full w-full object-cover"
					/>
					{hasOverlay && <TextOverlay heading={heading} subheading={subheading} />}
					<div
						className="absolute inset-0 z-[5] flex cursor-pointer items-center justify-center bg-black/0 transition-colors duration-200 group-hover/img:bg-black/40"
						onClick={(e) => {
							e.stopPropagation();
							openLightbox();
						}}
					>
						<span className="flex items-center gap-2 rounded-full bg-white/90 px-4 py-2 text-sm font-medium text-gray-700 opacity-0 shadow-lg backdrop-blur-sm transition-opacity duration-200 group-hover/img:opacity-100">
							<FiMaximize2 size={14} />
							Click to expand image
						</span>
					</div>
				</div>
				{lightboxOpen && (
					<Lightbox
						images={images}
						alt={alt}
						activeIndex={0}
						onClose={closeLightbox}
						onPrev={lightboxPrev}
						onNext={lightboxNext}
					/>
				)}
			</>
		);
	}

	return (
		<>
			<div className="group/carousel relative">
				{/* Scrollable image strip */}
				<div
					ref={scrollRef}
					onScroll={handleScroll}
					className={`${height} flex snap-x snap-mandatory overflow-x-auto scroll-smooth`}
					style={{ scrollbarWidth: "none", msOverflowStyle: "none", WebkitOverflowScrolling: "touch" }}
				>
					{images.map((image, index) => (
						<div
							key={index}
							className="h-full w-full shrink-0 snap-center"
						>
							<FallbackImage
								src={image}
								alt={`${alt} ${index + 1}`}
								className="h-full w-full object-cover"
							/>
						</div>
					))}
				</div>

				{/* Text overlay */}
				{hasOverlay && <TextOverlay heading={heading} subheading={subheading} />}

				{/* Hover expand overlay */}
				<div
					className="absolute inset-0 z-[5] flex cursor-pointer items-center justify-center bg-black/0 transition-colors duration-200 group-hover/carousel:bg-black/40"
					onClick={(e) => {
						e.stopPropagation();
						openLightbox();
					}}
				>
					<span className="flex items-center gap-2 rounded-full bg-white/90 px-4 py-2 text-sm font-medium text-gray-700 opacity-0 shadow-lg backdrop-blur-sm transition-opacity duration-200 group-hover/carousel:opacity-100">
						<FiMaximize2 size={14} />
						Click to expand image
					</span>
				</div>

				{/* Left arrow */}
				{activeIndex > 0 && (
					<button
						type="button"
						onClick={(e) => {
							e.stopPropagation();
							scrollTo(activeIndex - 1);
						}}
						className="absolute left-2 top-1/2 z-10 -translate-y-1/2 rounded-full bg-white/80 p-1.5 shadow-md backdrop-blur-sm transition-opacity hover:bg-white"
						aria-label="Previous image"
					>
						<FiChevronLeft size={18} />
					</button>
				)}

				{/* Right arrow */}
				{activeIndex < total - 1 && (
					<button
						type="button"
						onClick={(e) => {
							e.stopPropagation();
							scrollTo(activeIndex + 1);
						}}
						className="absolute right-2 top-1/2 z-10 -translate-y-1/2 rounded-full bg-white/80 p-1.5 shadow-md backdrop-blur-sm transition-opacity hover:bg-white"
						aria-label="Next image"
					>
						<FiChevronRight size={18} />
					</button>
				)}

				{/* Dot indicators */}
				<div className={`absolute left-1/2 flex -translate-x-1/2 gap-1.5 ${hasOverlay ? "bottom-4 right-4 left-auto translate-x-0" : "bottom-2"}`}>
					{images.map((_, index) => (
						<button
							key={index}
							type="button"
							onClick={(e) => {
								e.stopPropagation();
								scrollTo(index);
							}}
							className={`z-10 h-1.5 rounded-full transition-all ${index === activeIndex
								? "w-4 bg-white"
								: "w-1.5 bg-white/50"
								}`}
							aria-label={`Go to image ${index + 1}`}
						/>
					))}
				</div>
			</div>

			{/* Lightbox */}
			{lightboxOpen && (
				<Lightbox
					images={images}
					alt={alt}
					activeIndex={lightboxIndex}
					onClose={closeLightbox}
					onPrev={lightboxPrev}
					onNext={lightboxNext}
				/>
			)}
		</>
	);
};
